/**
 * The 2026 Typst archive: the course repository's faithful Typst port of the 2025 LaTeX
 * archive (`archive/2026/` in the course repository, Phase 8).
 *
 * Two kinds of files live there (see the repository's `archive/2026/CONVERSION.md`):
 *
 *   - pool fragments — `exercise-sheets/pool/<topic>/<name>.typ` and
 *     `tutorial-sheets/<X>/pool/<name>.typ` — each one or more
 *     `#exercise(difficulty:, points:, number:, competencies:)[Title][Body …]` calls after
 *     `#import "/archive/2026/compat.typ": *`, with the LaTeX file's leading comments
 *     (`// UNREFERENCED (from sheet I)`) kept, and now and then a `#let` helper of its own;
 *   - sheet files — `exercise-sheets/<X>/exerciseX.typ`, `tutorial-sheets/<X>/tutorialX.typ`
 *     — a `#show: exercise-sheet.with(…)` rule, `= Headings`, `#include "../pool/…"` (or
 *     `"pool/…"`), commented-out includes, and the odd exercise written inline (warm-ups).
 *
 * This module reads both (pure — no file system), names their exercises
 * (`a26/<path>#<n>`, the path relative to `archive/2026`, n counting the file's
 * `#exercise` calls from 1) and holds what matching them to the 2025 corpus needs: where a
 * 2025 source file's port lives, and how alike two titles or two texts are. The server
 * indexes the vendored snapshot with it (server/src/archive/index.ts).
 */
import {
  definitionsUsed,
  exerciseFileContext,
  findCourseExercises,
  splitArgs,
  unescapeTypstText,
  type CourseExerciseBlock,
} from './coursesheet-parse.js';
import { findTypstBlocks } from './typstscan.js';
import { stripTypstAnswer } from './typst.js';
import { unescapeTex } from './textutil.js';

/** Where the archive sits in the course repository and in Studio's course snapshot. */
export const ARCHIVE_ROOT = 'archive/2026';
/** The prefix of an archive exercise's library id. */
export const ARCHIVE_ID_PREFIX = 'a26/';
/** What every fragment imports first; a course sheet's exercise file needs it too (Lane T). */
export const ARCHIVE_COMPAT_IMPORT = '#import "/archive/2026/compat.typ": *';
/** The library source of archive exercises no 2025 corpus exercise matches. */
export const ARCHIVE_SOURCE_LABEL = 'Pool 2025 (Typst, unused)';
/**
 * Bumped whenever the parser or the matcher changes what they produce, so the server
 * re-indexes a snapshot it has indexed before.
 */
export const ARCHIVE_INDEX_VERSION = 3;

export const isArchiveId = (id: string | null | undefined): boolean => Boolean(id?.startsWith(ARCHIVE_ID_PREFIX));

/** `a26/tutorial-sheets/00/pool/getting_started.typ#1` */
export function archiveExerciseId(path: string, block: number): string {
  return `${ARCHIVE_ID_PREFIX}${path}#${block}`;
}

/** The path (relative to `archive/2026`) and 1-based block of an archive id, or null. */
export function parseArchiveId(id: string): { path: string; block: number } | null {
  if (!id.startsWith(ARCHIVE_ID_PREFIX)) return null;
  const m = /^(.+\.typ)#(\d+)$/.exec(id.slice(ARCHIVE_ID_PREFIX.length));
  if (!m) return null;
  const path = m[1]!;
  if (!isArchivePath(path)) return null;
  return { path, block: Number(m[2]) };
}

/** A clean relative path inside the archive: no `..`, no leading slash, no backslash. */
export function isArchivePath(path: string): boolean {
  return (
    /^[A-Za-z0-9_][A-Za-z0-9_.\-/ ]*\.typ$/.test(path) &&
    !path.split('/').some((seg) => seg === '' || seg === '.' || seg === '..')
  );
}

/* ------------------------------------------------------------- file kinds */

export type ArchiveFileKind = 'fragment' | 'sheet' | 'other';

/**
 * What a `.typ` file of the archive is, by where it lies: a pool fragment, a sheet file
 * (one level below `exercise-sheets/` or `tutorial-sheets/`, outside `pool/`), or
 * neither (`compat.typ`, `_check.typ`).
 */
export function archiveFileKind(path: string): ArchiveFileKind {
  const seg = path.split('/');
  if (!path.endsWith('.typ')) return 'other';
  if (seg[0] === 'exercise-sheets') {
    if (seg[1] === 'pool') return seg.length >= 3 ? 'fragment' : 'other';
    return seg.length === 3 ? 'sheet' : 'other';
  }
  if (seg[0] === 'tutorial-sheets') {
    if (seg.length === 3) return 'sheet';
    if (seg.length >= 4 && seg[2] === 'pool') return 'fragment';
  }
  return 'other';
}

/** The series a file belongs to, by its top directory. */
export function archiveSeries(path: string): 'exercise' | 'tutorial' | 'exam-prep' {
  if (path.startsWith('exercise-sheets/')) return 'exercise';
  if (/^tutorial-sheets\/[^/]*[Pp]rep/.test(path)) return 'exam-prep';
  return 'tutorial';
}

/**
 * The archive path a 2025 corpus source file was ported to: the same path under
 * `archive/2026`, `.typ` for `.tex` (CONVERSION.md, "Files"). `../2025/tutorial-sheets/00/pool/x.tex`
 * → `tutorial-sheets/00/pool/x.typ`; null for anything not in the 2025 archive's layout.
 */
export function archivePathFor2025(sourcePath: string | null | undefined): string | null {
  if (!sourcePath) return null;
  const m = /(?:^|\/)2025\/((?:exercise|tutorial)-sheets\/.+)\.tex$/.exec(sourcePath.replace(/\\/g, '/'));
  if (!m) return null;
  const path = `${m[1]}.typ`;
  return isArchivePath(path) ? path : null;
}

/* ------------------------------------------------------------- fragments */

export interface ArchiveBlock {
  /** 1-based position among the file's `#exercise` calls */
  index: number;
  /**
   * the title as plain words for cards, lists and warnings (`#link(…)[Insomnia]` →
   * `Insomnia`, `$append$ Associativity` → `append Associativity`); null without a title
   */
  title: string | null;
  /** the title as written, Typst markup and all (what a course sheet prints) */
  titleSource: string | null;
  difficulty: number | null;
  points: number | null;
  /** `number:` as written (`0`, `-1`, `text(…)[…]`), null without one */
  number: string | null;
  competencies: string[];
  hasSolution: boolean;
  hasMarking: boolean;
  /** the call as written, `#exercise(…)[…][…]` */
  block: string;
  /** the content of the body brackets, dedented — a Studio Typst body */
  body: string;
  /**
   * what the block needs from the rest of its file, in file order: the file's own imports
   * (`#import "@preview/curryst:0.6.0"`, compat.typ's left out) and the `#let`
   * definitions the block (or its preamble) uses
   */
  helpers: string[];
  /**
   * text the file sets before the call that belongs to it — an explanation the exercise
   * builds on (`StackCamelDescription2.typ`'s description of StackCaml); null for none
   */
  preamble: string | null;
  /** helpers + preamble + the call: what compiles on its own after `ARCHIVE_COMPAT_IMPORT` */
  code: string;
  /** helpers + preamble + body: what an item on a sheet holds (a body, no call around it) */
  studioBody: string;
  /** the statement's words, solutions and markup left out — for search and the card */
  plainText: string;
  /** the same with the solutions and marking notes — what a corpus text is compared with */
  matchText: string;
}

export interface ParsedArchiveFragment {
  /**
   * The text after a `// UNREFERENCED` marker (`from sheet I`, `alternative to …`, '' for
   * a bare marker); null when the file has none. The LaTeX archive marked pool files no
   * sheet used this way.
   */
  unreferenced: string | null;
  /** the comment lines before the first statement, as written (without `// `) */
  leadingComments: string[];
  blocks: ArchiveBlock[];
}

/** `("A", "B")`, `(1, 2)`, `"A"` → the items as text. */
function listArg(value: string | undefined): string[] {
  if (!value) return [];
  const v = value.trim();
  const inner = v.startsWith('(') && v.endsWith(')') ? v.slice(1, -1) : v;
  return inner
    .split(',')
    .map((s) => s.trim().replace(/^"(.*)"$/, '$1'))
    .filter(Boolean);
}

const hasBlock = (body: string, name: string) => findTypstBlocks(body, [name]).length > 0;

/** Read one pool fragment (or the inline exercises of a sheet file). Never throws. */
export function parseArchiveFragment(src: string): ParsedArchiveFragment {
  const text = src.replace(/\r\n/g, '\n');
  const leadingComments: string[] = [];
  for (const line of text.split('\n')) {
    const t = line.trim();
    if (!t) continue;
    if (!t.startsWith('//')) break;
    leadingComments.push(t.replace(/^\/\/\s?/, ''));
  }
  let unreferenced: string | null = null;
  for (const c of leadingComments) {
    const m = /^UNREFERENCED\b\s*[,;:]?\s*(.*)$/.exec(c);
    if (m) {
      unreferenced = m[1]!.replace(/^\((.*)\)$/, '$1').trim();
      break;
    }
  }
  const calls = findCourseExercises(text, { dedent: true }).filter((b) => b.fn === 'exercise');
  const context = exerciseFileContext(text, calls);
  const imports = ownImports(text, calls);
  const blocks = calls.map((call, k) =>
    toBlock(text, call, k + 1, {
      lets: context.lets,
      imports,
      preamble: preambleOf(context.texts.filter((t) => t.before === k).map((t) => t.text)),
    }),
  );
  return { unreferenced, leadingComments, blocks };
}

/**
 * The fragment's own top-level `#import`s, outside its calls, compat.typ's left out: a
 * block may call what they bind (`curryst.rule(…)`), and a body carries them along (an
 * import is fine inside a content block). Each statement as written, multi-line ones
 * whole.
 */
function ownImports(src: string, calls: readonly CourseExerciseBlock[]): string[] {
  const out: string[] = [];
  const inside = (at: number) => calls.some((c) => at >= c.start && at < c.end);
  for (const m of src.matchAll(/^[ \t]*#import\b/gm)) {
    const at = m.index! + m[0].indexOf('#');
    if (inside(at)) continue;
    // to the end of the line, and on while a parenthesis is open (`#import "x": (a,\n b)`)
    let end = at;
    let depth = 0;
    for (; end < src.length; end++) {
      const c = src[end];
      if (c === '"') {
        end++;
        while (end < src.length && src[end] !== '"' && src[end] !== '\n') end += src[end] === '\\' ? 2 : 1;
      } else if (c === '(') depth++;
      else if (c === ')') depth--;
      else if (c === '\n' && depth <= 0) break;
    }
    const stmt = src.slice(at, end).replace(/\s*\/\/.*$/, '').trimEnd();
    if (/^#import\s+"\/archive\/2026\/compat\.typ"/.test(stmt)) continue;
    if (!out.includes(stmt)) out.push(stmt);
  }
  return out;
}

/**
 * The text a file sets before an exercise, as the exercise's preamble: headings left out
 * (they are the sheet's structure — a body has none), the rest as written.
 */
function preambleOf(texts: string[]): string | null {
  const text = texts
    .join('\n\n')
    .split('\n')
    .filter((line) => !/^\s*(?:#heading\b.*|=+\s.*)$/.test(line))
    .join('\n')
    .replace(/^(?:[ \t]*\n)+/, '')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd();
  return text.trim() ? text : null;
}

function toBlock(
  src: string,
  call: CourseExerciseBlock,
  index: number,
  context: { lets: ReturnType<typeof exerciseFileContext>['lets']; imports: string[]; preamble: string | null },
): ArchiveBlock {
  const block = src.slice(call.start, call.end);
  const { preamble } = context;
  const helpers = [...context.imports, ...definitionsUsed(preamble ? `${preamble}\n${block}` : block, context.lets)];
  const prefix = helpers.length ? `${helpers.join('\n')}\n\n` : '';
  const lead = preamble ? `${preamble}\n\n` : '';
  const number = call.args.number?.trim();
  return {
    index,
    title: call.titleSource ? typstInlineText(call.titleSource) || null : call.title,
    titleSource: call.titleSource,
    difficulty: call.difficulty,
    points: call.points,
    number: number ? number : null,
    competencies: listArg(call.args.competencies),
    hasSolution: hasBlock(call.body, 'solution') || hasBlock(call.body, 'answer-space') || /\bis-solution\(\)/.test(call.body),
    hasMarking: hasBlock(call.body, 'marking'),
    block,
    body: call.body,
    helpers,
    preamble,
    code: prefix + lead + block,
    studioBody: prefix + lead + call.body,
    // the statement alone: the title has a field (and a search column) of its own
    plainText: typstPlainText(lead + call.body),
    matchText: typstPlainText(`${call.titleSource ?? ''}\n${lead}${call.body}`, { keepSolutions: true }),
  };
}

/**
 * The words of a Typst statement: solutions and marking notes out (unless
 * `keepSolutions`), comments out, function names, their named arguments and markup
 * characters out; the text of content blocks, raw spans and the words of maths kept
 * (`#grid(columns: (7fr, 3fr), [A], [B])` → `A B`, `$mi("push") space 4$` → `push 4`).
 */
export function typstPlainText(source: string, opts: { keepSolutions?: boolean } = {}): string {
  return markupWords(opts.keepSolutions ? source : stripTypstAnswer(source))
    .replace(/[[\]`$*_{}()#"<>=+|\\]/g, ' ')
    // list and enum markers, alone on what is left of their line
    .replace(/(^|\s)[-+/&]+(?=\s|$)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * A line of Typst markup — a title — as plain text: `#link("…")[Insomnia]` → `Insomnia`,
 * `$append$ Associativity` → `append Associativity`, `You're my type$tilde tilde$` →
 * `You're my type`; escapes undone, emphasis marks gone.
 */
export function typstInlineText(source: string): string {
  return markupWords(source)
    .replace(/[*_`]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?)])/g, '$1')
    .trim();
}

/** Names in maths that are notation rather than a word of the statement. */
const MATH_NOTATION = new Set(
  (
    'space thin med thick quad wide dots dot times tilde arrow in NN ZZ RR QQ BB CC and or not forall exists ' +
    'sum prod lim oo infinity emptyset nothing union sect subset supset plus minus cdot eq approx equiv top bot ' +
    'prime compose without sim ell divides ast star lt gt le ge ne colon semi comma bar hat angle angles mod'
  ).split(' '),
);

const IDENT = /^[A-Za-z_][\w-]*(?:\.[A-Za-z_][\w-]*)*/;
const KEYWORDS = new Set(['let', 'set', 'show', 'import', 'include']);
/** Calls whose arguments are no words of the statement: labels, references, files, spacing. */
const NO_WORDS = new Set(['label', 'ex-label', 'ref', 'ex-ref', 'cite', 'image', 'read', 'h', 'v', 'pagebreak', 'colbreak', 'counter', 'state']);

/** Where a raw span opened at `i` ends (the index after its closing fence) and its text. */
function rawSpan(s: string, i: number): { end: number; text: string } {
  const fence = /^`{3,}/.exec(s.slice(i))?.[0] ?? '`';
  const close = s.indexOf(fence, i + fence.length);
  const stop = close < 0 ? s.length : close;
  let text = s.slice(i + fence.length, stop);
  if (fence.length >= 3) text = text.replace(/^[A-Za-z0-9_+-]*(?=\s)/, '');
  return { end: close < 0 ? s.length : close + fence.length, text };
}

/** Where a string literal opened at `i` ends (after its quote) and its value. */
function stringSpan(s: string, i: number): { end: number; text: string } {
  let j = i + 1;
  let text = '';
  while (j < s.length && s[j] !== '"') {
    if (s[j] === '\\') {
      text += s[j + 1] ?? '';
      j += 2;
    } else text += s[j++];
  }
  return { end: Math.min(j + 1, s.length), text };
}

/** Markup from `i` to the `]` that closes it (or the end): its words, and the index after. */
function readMarkup(s: string, i: number, closer: boolean): { end: number; text: string } {
  let out = '';
  let depth = 0;
  while (i < s.length) {
    const c = s[i]!;
    if (c === '\\') {
      out += s[i + 1] === '\n' ? ' ' : (s[i + 1] ?? '');
      i += 2;
    } else if (c === '`') {
      const r = rawSpan(s, i);
      out += ` ${r.text} `;
      i = r.end;
    } else if (c === '$') {
      const m = readMath(s, i + 1);
      out += ` ${m.text} `;
      i = m.end;
    } else if (c === '/' && s[i + 1] === '/' && s[i - 1] !== ':') {
      const eol = s.indexOf('\n', i);
      i = eol < 0 ? s.length : eol;
    } else if (c === '/' && s[i + 1] === '*') {
      const e = s.indexOf('*/', i + 2);
      i = e < 0 ? s.length : e + 2;
    } else if (c === '#') {
      const r = readHash(s, i + 1);
      out += ` ${r.text} `;
      i = r.end;
    } else if (c === '<' && /^<[\w:.-]+>/.test(s.slice(i))) {
      i = s.indexOf('>', i) + 1; // a label
    } else if (c === '[') {
      depth++;
      out += ' ';
      i++;
    } else if (c === ']') {
      if (depth === 0 && closer) return { end: i + 1, text: out };
      depth = Math.max(0, depth - 1);
      out += ' ';
      i++;
    } else if (c === '~') {
      out += ' ';
      i++;
    } else {
      out += c;
      i++;
    }
  }
  return { end: s.length, text: out };
}

/** After a `#`: a call's content arguments and blocks, a statement skipped, a block of code skipped. */
function readHash(s: string, i: number): { end: number; text: string } {
  if (s[i] === '[') return readMarkup(s, i + 1, true);
  if (s[i] === '(' || s[i] === '{') return { end: readCode(s, i).end, text: '' };
  const name = IDENT.exec(s.slice(i))?.[0];
  if (!name) return { end: i, text: '#' };
  i += name.length;
  if (NO_WORDS.has(name)) return { end: readCallTail(s, i).end, text: ' ' };
  if (KEYWORDS.has(name)) {
    // a statement: to the end of its line, and on while a bracket is open
    let depth = 0;
    for (; i < s.length; i++) {
      const c = s[i];
      if (c === '"') i = stringSpan(s, i).end - 1;
      else if (c === '(' || c === '[' || c === '{') depth++;
      else if (c === ')' || c === ']' || c === '}') depth--;
      else if (c === '\n' && depth <= 0) break;
    }
    return { end: i, text: '' };
  }
  return readCallTail(s, i);
}

/** A call's argument lists and trailing content blocks, from just after its name. */
function readCallTail(s: string, i: number): { end: number; text: string } {
  let text = '';
  for (;;) {
    if (s[i] === '(') {
      const r = readCode(s, i);
      text += ` ${r.text} `;
      i = r.end;
    } else if (s[i] === '[') {
      const r = readMarkup(s, i + 1, true);
      text += ` ${r.text} `;
      i = r.end;
    } else return { end: i, text };
  }
}

/**
 * Code from the bracket at `i` to the one that closes it: the words of the content blocks
 * in it, and of the strings given as plain arguments (`#oc("if y then x")`) — names,
 * named arguments (`columns: 2`, `numbering: "(i)"`), numbers, and strings that are a
 * link or a file are code.
 */
function readCode(s: string, i: number): { end: number; text: string } {
  let depth = 0;
  let text = '';
  // where the argument being read began, per depth
  const argStart: number[] = [];
  while (i < s.length) {
    const c = s[i]!;
    if (c === '"') {
      const r = stringSpan(s, i);
      const before = s.slice(argStart.at(-1) ?? i, i);
      const named = /[A-Za-z_][\w-]*\s*:\s*$/.test(before);
      const address = /:\/\/|^[@/.]|\.(?:png|jpe?g|svg|gif|pdf|typ|csv|json|ml)$/i.test(r.text);
      if (!named && !address) text += ` ${r.text} `;
      i = r.end;
    } else if (c === '[') {
      const r = readMarkup(s, i + 1, true);
      text += ` ${r.text} `;
      i = r.end;
    } else if (c === '/' && s[i + 1] === '/') {
      const eol = s.indexOf('\n', i);
      i = eol < 0 ? s.length : eol;
    } else if (c === '(' || c === '{') {
      depth++;
      i++;
      argStart.push(i);
    } else if (c === ')' || c === '}') {
      depth--;
      i++;
      argStart.pop();
      if (depth <= 0) return { end: i, text };
    } else {
      if (c === ',' && argStart.length) argStart[argStart.length - 1] = i + 1;
      i++;
    }
  }
  return { end: s.length, text };
}

/** Maths from just after its `$` to just after the closing one: its words. */
function readMath(s: string, i: number): { end: number; text: string } {
  const words: string[] = [];
  while (i < s.length && s[i] !== '$') {
    const c = s[i]!;
    if (c === '\\') i += 2;
    else if (c === '"') {
      const r = stringSpan(s, i);
      words.push(r.text);
      i = r.end;
    } else if (c === '#') {
      const r = readHash(s, i + 1);
      words.push(r.text);
      i = Math.max(r.end, i + 1);
    } else if (/[A-Za-z0-9]/.test(c)) {
      const m = /^(?:[A-Za-z][A-Za-z0-9]*(?:\.[A-Za-z]+)*|\d+(?:\.\d+)?)/.exec(s.slice(i))![0];
      i += m.length;
      const call = s[i] === '(';
      if (/^\d/.test(m) || m.length === 1) words.push(m);
      else if (!call && !m.includes('.') && !MATH_NOTATION.has(m)) words.push(m);
    } else i++;
  }
  return { end: Math.min(i + 1, s.length), text: words.join(' ') };
}

/** The words of a stretch of markup. */
function markupWords(source: string): string {
  return readMarkup(source, 0, false).text;
}

/* ------------------------------------------------------------------ sheets */

export type ArchiveSheetEntry =
  /** `#include "…"` (or a commented-out one), resolved to an archive path */
  | { kind: 'include'; path: string; commented: boolean; note?: string }
  /** an `#exercise` written in the sheet file itself: its block number there */
  | { kind: 'exercise'; block: number }
  | { kind: 'heading'; text: string };

export interface ParsedArchiveSheet {
  /** the template function the sheet is shown with (`exercise-sheet`, `tutorial-sheet`, …) */
  fn: string | null;
  /** `number:` of the template call, as text ("A", "0", "X") */
  number: string | null;
  /** the title as plain text */
  title: string | null;
  /** `// BUILD name: args` lines */
  builds: { name: string; args: string }[];
  entries: ArchiveSheetEntry[];
}

/** `../pool/x.typ` from `exercise-sheets/A/exerciseA.typ` → `exercise-sheets/pool/x.typ`; null outside the archive. */
export function resolveArchiveInclude(fromPath: string, target: string): string | null {
  let rel: string;
  if (target.startsWith('/')) {
    if (!target.startsWith(`/${ARCHIVE_ROOT}/`)) return null;
    rel = target.slice(ARCHIVE_ROOT.length + 2);
  } else {
    const parts = fromPath.split('/').slice(0, -1);
    for (const seg of target.split('/')) {
      if (seg === '..') {
        if (!parts.length) return null;
        parts.pop();
      } else if (seg && seg !== '.') parts.push(seg);
    }
    rel = parts.join('/');
  }
  return isArchivePath(rel) ? rel : null;
}

const plainContent = (source: string): string =>
  unescapeTypstText(source.replace(/\s+/g, ' ').trim());

/** Read a sheet file of the archive. `path` is its own archive path, for its includes. Never throws. */
export function parseArchiveSheet(src: string, path: string): ParsedArchiveSheet {
  const text = src.replace(/\r\n/g, '\n');
  const out: ParsedArchiveSheet = { fn: null, number: null, title: null, builds: [], entries: [] };
  for (const m of text.matchAll(/^\/\/\s*BUILD\s+([^\s:]+)\s*:\s*(.*)$/gm)) {
    out.builds.push({ name: m[1]!, args: m[2]!.trim() });
  }
  const show = /#show:\s*([A-Za-z][\w-]*)\.with\(/.exec(text);
  if (show) {
    out.fn = show[1]!;
    const open = show.index + show[0].length - 1;
    const args = splitArgs(text, open)?.args ?? [];
    for (const a of args) {
      if (a.name === 'number') out.number = a.value.replace(/^"(.*)"$/, '$1');
      if (a.name === 'title') {
        const v = a.value.trim();
        out.title = v.startsWith('[') && v.endsWith(']') ? plainContent(v.slice(1, -1)) : v.replace(/^"(.*)"$/, '$1');
      }
    }
  }

  // positions of everything the sheet lists, sorted by where it is written
  const events: { at: number; entry: ArchiveSheetEntry }[] = [];
  const calls = findCourseExercises(text).filter((b) => b.fn === 'exercise');
  calls.forEach((call, k) => events.push({ at: call.start, entry: { kind: 'exercise', block: k + 1 } }));
  const inside = (at: number) => calls.some((c) => at > c.start && at < c.end);
  for (const m of text.matchAll(/^([ \t]*)(\/\/[ \t]*)?#include[ \t]+"([^"]+)"[ \t]*(?:\/\/[ \t]*(.*))?$/gm)) {
    if (inside(m.index!)) continue;
    const resolved = resolveArchiveInclude(path, m[3]!);
    if (!resolved) continue;
    const note = m[4]?.trim();
    events.push({
      at: m.index!,
      entry: { kind: 'include', path: resolved, commented: Boolean(m[2]), ...(note ? { note } : {}) },
    });
  }
  for (const m of text.matchAll(/^(=+)[ \t]+(.+)$/gm)) {
    if (inside(m.index!)) continue;
    events.push({ at: m.index!, entry: { kind: 'heading', text: plainContent(m[2]!) } });
  }
  out.entries = events.sort((a, b) => a.at - b.at).map((e) => e.entry);
  return out;
}

/** The archive paths a sheet really includes, in order (commented-out ones left out). */
export function archiveSheetIncludes(sheet: ParsedArchiveSheet): string[] {
  return sheet.entries
    .filter((e): e is Extract<ArchiveSheetEntry, { kind: 'include' }> => e.kind === 'include' && !e.commented)
    .map((e) => e.path);
}

/* ---------------------------------------------------------------- matching */

/**
 * A title or a text reduced to lower-case words, whichever markup it was written in:
 * LaTeX commands, Typst calls, escapes, maths delimiters and punctuation go, the words
 * stay. `So much \lstinline[]|fun|` and ``So much `fun` `` are the same then, as are
 * `\href{u}{Order!}` and `#link("u")[Order!]`.
 */
export function normaliseForMatch(text: string | null | undefined): string {
  if (!text) return '';
  let s = text
    .replace(/\\lstinline(?:\[[^\]]*\])?\s*([|!+])(.*?)\1/g, ' $2 ')
    .replace(/\\href\s*\{[^}]*\}\s*\{([^}]*)\}/g, ' $1 ')
    .replace(/#link\("[^"]*"\)(?:\[([^\]]*)\])?/g, ' $1 ');
  s = unescapeTex(s);
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/#[a-z][\w.-]*/g, ' ')
    .replace(/[^a-z0-9ß]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const bigrams = (s: string): Map<string, number> => {
  const out = new Map<string, number>();
  const t = s.replace(/\s+/g, ' ');
  for (let i = 0; i < t.length - 1; i++) {
    const g = t.slice(i, i + 2);
    out.set(g, (out.get(g) ?? 0) + 1);
  }
  return out;
};

/**
 * How alike two titles are, 0…1: the Dice coefficient of their character pairs after
 * `normaliseForMatch`. Two missing titles are alike (1); one missing is not (0).
 */
export function titleSimilarity(a: string | null | undefined, b: string | null | undefined): number {
  const x = normaliseForMatch(a);
  const y = normaliseForMatch(b);
  if (!x && !y) return 1;
  if (!x || !y) return 0;
  if (x === y) return 1;
  const gx = bigrams(x);
  const gy = bigrams(y);
  let common = 0;
  let total = 0;
  for (const n of gx.values()) total += n;
  for (const n of gy.values()) total += n;
  for (const [g, n] of gx) common += Math.min(n, gy.get(g) ?? 0);
  return total ? (2 * common) / total : 0;
}

/**
 * How alike two texts are, 0…1: the Dice coefficient of their bags of words (numbers and
 * one-letter names included — `{1, 2} ∩ {2, 3}` is mostly numbers). A translation keeps
 * its identifiers, numbers and most nouns, which is what this counts; layout and markup
 * do not reach it. The corpus's own headings (`Exercise EA.3`, `Proposed Solution EA.3`)
 * are not part of either text.
 */
export function textSimilarity(a: string | null | undefined, b: string | null | undefined): number {
  const words = (s: string | null | undefined) => {
    const bag = new Map<string, number>();
    const text = (s ?? '').replace(/^\s*(?:Exercise|Proposed Solution|Solution|Aufgabe|L(?:ö|oe)sungsvorschlag)\s+[A-Z]*\d*\.?-?\d+\s*$/gm, ' ');
    for (const w of normaliseForMatch(text).split(' ')) if (w) bag.set(w, (bag.get(w) ?? 0) + 1);
    return bag;
  };
  const x = words(a);
  const y = words(b);
  let nx = 0;
  let ny = 0;
  for (const n of x.values()) nx += n;
  for (const n of y.values()) ny += n;
  if (!nx && !ny) return 1;
  if (!nx || !ny) return 0;
  let common = 0;
  for (const [w, n] of x) common += Math.min(n, y.get(w) ?? 0);
  return (2 * common) / (nx + ny);
}

/**
 * The confidence that an archive block is the port of a corpus exercise, 0…1, from their
 * titles and texts. Titles say most where both have one; a text says the rest, and is
 * all there is for two untitled exercises.
 */
export function matchConfidence(
  corpus: { title: string | null; text: string | null },
  archive: { title: string | null; text: string | null },
): number {
  const titled = Boolean(normaliseForMatch(corpus.title)) && Boolean(normaliseForMatch(archive.title));
  const text = textSimilarity(corpus.text, archive.text);
  if (!titled) {
    // one titled and the other not is odd for a faithful port, but the text decides
    return Math.round(text * 1000) / 1000;
  }
  const title = titleSimilarity(corpus.title, archive.title);
  return Math.round((0.6 * title + 0.4 * text) * 1000) / 1000;
}

/**
 * How much of `part`'s text is in `whole`, 0…1: the share of `part`'s words (counted with
 * their multiplicity) that `whole` has too. A 2025 item that printed two exercises of a
 * file holds nearly all words of the second one.
 */
export function textContainment(whole: string | null | undefined, part: string | null | undefined): number {
  const bag = (s: string | null | undefined) => {
    const out = new Map<string, number>();
    for (const w of normaliseForMatch(s).split(' ')) if (w) out.set(w, (out.get(w) ?? 0) + 1);
    return out;
  };
  const a = bag(whole);
  const b = bag(part);
  let n = 0;
  let common = 0;
  for (const [w, k] of b) {
    n += k;
    common += Math.min(k, a.get(w) ?? 0);
  }
  return n ? common / n : 0;
}

/**
 * Whether a 2025 corpus exercise's text holds an archive block besides the one it is
 * matched with: the block's title is in it (when it has one) and nearly all its words.
 */
export function corpusHoldsBlock(corpusText: string | null | undefined, block: Pick<ArchiveBlock, 'title' | 'matchText'>): boolean {
  const title = normaliseForMatch(block.title);
  if (title && !` ${normaliseForMatch(corpusText)} `.includes(` ${title} `)) return false;
  return textContainment(corpusText, block.matchText) >= 0.75;
}

/**
 * A stand-in, not an exercise: an untitled block without a solution that only says the
 * exercise is not shown yet (`00-misc/hidden-from-the-preview.typ`, "…is therefore not
 * included in the preview"). The index leaves these out, so they are never picked.
 */
export function isArchivePlaceholder(block: Pick<ArchiveBlock, 'title' | 'hasSolution' | 'hasMarking' | 'plainText'>): boolean {
  if (block.title || block.hasSolution || block.hasMarking) return false;
  return (
    block.plainText.length < 400 &&
    /\b(?:not (?:included|shown) in the preview|hidden from the preview|will be (?:published|added|shown) later)\b/i.test(block.plainText)
  );
}

/** Below this the path-and-order pair is reported as a mismatch and not linked. */
export const ARCHIVE_MATCH_MIN = 0.35;
/** Below this a link is kept but listed for a look (`archive_exercises.match_confidence`). */
export const ARCHIVE_MATCH_REVIEW = 0.6;

/* ------------------------------------------------------------------ topics */

/**
 * The pool's directories and the 2025 tutorial sheets, onto the library's topic
 * vocabulary (taxonomy.ts TOPICS). Topic tags of an archive exercise no corpus exercise
 * matches are derived from where it lies; a tutor can change them like any other tags.
 */
const POOL_TOPICS: Record<string, string[]> = {
  '00-misc': ['misc'],
  '01-getting-started': ['intro-programming'],
  '02-constructing-and-executing-programs': ['abstract-syntax', 'environments'],
  '03-higher-order-functions': ['higher-order-functions'],
  '04-lists': ['lists'],
  '05-sorting': ['sorting'],
  '06-sum-types-exceptions': ['constructors-datatypes', 'options-exceptions'],
  '07-trees': ['trees'],
  '08-static-dynamic-semantics': ['static-semantics', 'dynamic-semantics'],
  '09-concrete-syntax': ['concrete-syntax'],
  '10-math-specs': ['math-functions'],
  '11-induction': ['induction'],
  '12-time-complexity': ['runtime-analysis'],
  '13-data-structures': ['abstract-data-types'],
  '14-state': ['references-mutability', 'imperative-programming'],
  'a-mathematical-foundations/01-sets': ['sets-relations'],
  'a-mathematical-foundations/02-propositions': ['boolean-logic'],
  'a-mathematical-foundations/03-tuples': ['tuples'],
  'a-mathematical-foundations/04-relations': ['sets-relations'],
  'a-mathematical-foundations/05-directed-graphs': ['graphs'],
  'a-mathematical-foundations/06-functions': ['math-functions'],
  'a-mathematical-foundations/07-termination': ['termination-proofs'],
};

/** The 2025 tutorial sheets by label, after their titles ("Lists & Folding" …). */
const TUTORIAL_TOPICS: Record<string, string[]> = {
  '00': ['intro-programming'],
  A: ['intro-programming', 'recursion'],
  B: ['abstract-syntax', 'environments'],
  C: ['polymorphism', 'iteration-iter'],
  D: ['lists', 'fold'],
  E: ['fold', 'sorting'],
  F: ['constructors-datatypes', 'trees'],
  G: ['trees'],
  H: ['static-semantics', 'dynamic-semantics'],
  I: ['type-checking', 'dynamic-semantics'],
  J: ['tokenization', 'parsing'],
  K: ['math-functions'],
  L: ['induction'],
  M: ['runtime-analysis'],
  N: ['imperative-programming'],
  Endterm_Prep_Sheet: ['exam-prep'],
};

/** Topic tags for an archive file, from its directory; [] where the directory says nothing. */
export function archiveTopics(path: string): string[] {
  const seg = path.split('/');
  if (seg[0] === 'exercise-sheets' && seg[1] === 'pool') {
    const two = `${seg[2]}/${seg[3]}`;
    return POOL_TOPICS[two] ?? POOL_TOPICS[seg[2] ?? ''] ?? [];
  }
  if (seg[0] === 'tutorial-sheets') return TUTORIAL_TOPICS[seg[1] ?? ''] ?? [];
  return [];
}

/**
 * A short place for a card's chip: the file (`level`, `strict-sorting #2`), a tutorial
 * pool's with its sheet (`TD/stasi`), an exercise written in a sheet file by that sheet
 * (`EA #2`). The full path is the card's sheet line.
 */
export function archiveLabel(path: string, block: number, blocks = 1): string {
  const seg = path.split('/');
  const stem = (seg[seg.length - 1] ?? '').replace(/\.typ$/, '');
  let where: string;
  if (seg[0] === 'exercise-sheets' && seg[1] === 'pool') where = stem;
  else if (seg[0] === 'tutorial-sheets' && seg[2] === 'pool') where = `${tutorialPlace(seg[1] ?? '')}/${stem}`;
  else if (seg[0] === 'tutorial-sheets') where = tutorialPlace(seg[1] ?? '');
  else where = `E${seg[1] ?? ''}`;
  return blocks > 1 || block > 1 ? `${where} #${block}` : where;
}

/**
 * A tutorial directory as a label: `T0`, `TD` for the lettered sheets, `Endterm` for the
 * end-term preparation sheet, the name itself (underscores as spaces) for anything else.
 */
function tutorialPlace(dir: string): string {
  if (dir === '00') return 'T0';
  if (/^[A-Za-z0-9]{1,2}$/.test(dir)) return `T${dir}`;
  if (/^endterm/i.test(dir)) return 'Endterm';
  return dir.replace(/_/g, ' ');
}

/* --------------------------------------------------------------- API shapes */

/** One archive exercise as `/api/archive` and the library hand it out. */
export interface ArchiveExerciseInfo {
  id: string;
  /** relative to `archive/2026` */
  path: string;
  blockIndex: number;
  title: string | null;
  difficulty: number | null;
  points: number | null;
  number: string | null;
  competencies: string[];
  hasSolution: boolean;
  hasMarking: boolean;
  /** the call as written, with the helpers it needs before it */
  code: string;
  /** helpers + body, the form an item on a sheet holds */
  body: string;
  /** what the block needs from the rest of its file: its imports and `#let` helpers ('' for none) */
  helpers: string;
  /** set when the 2025 file was marked `% UNREFERENCED` ('' for a bare marker) */
  unreferencedFrom: string | null;
  /** the archive sheet that includes the file, if any (a 2025 sheet used it) */
  includedBy: string | null;
  /**
   * Set by the exercise detail when the sheet that holds it is one only the archive has
   * (the end-term preparation sheet, Phase 8): its path. No 2025 sheet of Studio's had it.
   */
  archiveOnlySheet?: string | null;
  /**
   * Set by the exercise detail: the names the block uses that only the course template
   * has — `compat.typ`'s and the course headers' (`syntax-tree`, `dot-tree`, `wrt`, …),
   * less what Studio's own template has (server/src/archive/vocabulary.ts). Non-empty
   * means the block does not build on a sheet in Studio's format.
   */
  courseOnly?: string[];
  /** the 2025 corpus exercise this is the port of */
  corpusId: string | null;
  /**
   * Every archive exercise the 2025 corpus exercise holds, in order — one for most, two
   * where the 2025 ingest took two exercises of a file as one (`strict-sorting.tex`: EE.15
   * and EE.16 in one item). Switching such an item to one of them would drop the other.
   * [] without a corpus exercise.
   */
  parts: string[];
  matchConfidence: number | null;
  topics: string[];
  /** the course snapshot the row was indexed from */
  snapshot: string;
}

export interface ArchiveSheetInfo {
  path: string;
  kind: 'exercise' | 'tutorial' | 'exam-prep';
  label: string;
  title: string | null;
  corpusSheetId: string | null;
  /** the sheet's entries in order; an include lists the ids of the file's exercises */
  includes: (ArchiveSheetEntry & { ids?: string[] })[];
}

export interface ArchiveStatus {
  /** the snapshot indexed, null when the course template has no archive */
  snapshot: string | null;
  commit: string | null;
  indexedAt: string | null;
  exercises: number;
  /** of those, linked to a 2025 corpus exercise */
  matched: number;
  /** linked, but below `ARCHIVE_MATCH_REVIEW` */
  lowConfidence: number;
  /** without a 2025 exercise: the library source "Pool 2025 (Typst, unused)" */
  unmatched: number;
  sheets: number;
  /** 2025 corpus exercises with no archive block */
  corpusWithout: number;
  /** files that could not be read */
  failed: number;
}

export interface ArchiveMatchReport {
  status: ArchiveStatus;
  /** linked pairs below `ARCHIVE_MATCH_REVIEW` */
  lowConfidence: { corpusId: string; corpusTitle: string | null; archiveId: string; archiveTitle: string | null; confidence: number }[];
  /** path-and-order pairs below `ARCHIVE_MATCH_MIN`: not linked */
  mismatches: { corpusId: string; corpusTitle: string | null; archiveId: string; archiveTitle: string | null; confidence: number }[];
  /** 2025 corpus exercises whose source has no port (or no block left for them) */
  corpusWithout: { corpusId: string; title: string | null; sourcePath: string | null; expected: string | null }[];
  /** archive exercises without a 2025 exercise */
  unmatched: { id: string; title: string | null; includedBy: string | null; unreferencedFrom: string | null }[];
  /** 2025 corpus exercises that hold more than one archive exercise, with all of them */
  parts: { corpusId: string; archiveIds: string[] }[];
  /** placeholder blocks left out of the index ("not included in the preview") */
  placeholders: string[];
  failed: { path: string; error: string }[];
}
